import { useState, useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, Paper, Text, Stack, PasswordInput, Alert } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { setAdminSession } from '../../lib/auth'
import { supabase } from '@/lib/supabase'
import { getDeviceId } from '@/lib/deviceId'
import styles from './login.module.css'

export const Route = createFileRoute('/admin/login')({
  component: AdminLogin,
})

function AdminLogin() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLockedOut, setIsLockedOut] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)

  // Check lockout status on mount
  useEffect(() => {
    const checkLockoutStatus = async () => {
      try {
        const deviceId = getDeviceId()
        const { data } = await supabase
          .from('login_attempts')
          .select('lockout_until')
          .eq('device_id', deviceId)
          .eq('lockout_type', 'admin_login_lockout')
          .maybeSingle()

        if (data?.lockout_until) {
          const lockoutUntil = new Date(data.lockout_until).getTime()
          const now = Date.now()
          if (now < lockoutUntil) {
            setIsLockedOut(true)
            const updateCountdown = () => {
              const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))
              setRemainingSeconds(remaining)
              if (remaining === 0) {
                setIsLockedOut(false)
              }
            }
            updateCountdown()
            const interval = setInterval(updateCountdown, 1000)
            return () => clearInterval(interval)
          }
        }
      } catch (err) {
        console.error('Error checking lockout status:', err)
      }
    }
    checkLockoutStatus()
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isLockedOut) {
      return
    }

    setError(null)
    setLoading(true)

    try {
      const deviceId = await getDeviceId()
      
      // Call database function that handles password validation and lockout
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'validate_admin_password_with_lockout',
        {
          p_password: password,
          p_device_id: deviceId,
          p_lockout_type: 'admin_login_lockout',
        }
      )

      if (rpcError) {
        console.error('Error validating password:', rpcError)
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.')
        return
      }

      // Type assertion for the JSONB response
      const data = rpcData as {
        success: boolean
        error?: 'LOCKED_OUT' | 'INVALID_PASSWORD'
        lockout_until?: string
        remaining_seconds?: number
      }

      if (data.success) {
        // Password is valid, set session and navigate
        setAdminSession()
        navigate({
          to: '/admin',
          search: {
            page: 1,
            pageSize: 5,
            sortField: 'name',
            sortDirection: 'asc',
            nameFilter: '',
            statusFilter: '',
          },
        })
      } else if (data.error === 'LOCKED_OUT') {
        // Locked out - show countdown
        setIsLockedOut(true)
        if (data.lockout_until) {
          const lockoutUntil = new Date(data.lockout_until).getTime()
          const updateCountdown = () => {
            const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))
            setRemainingSeconds(remaining)
            if (remaining === 0) {
              setIsLockedOut(false)
            }
          }
          updateCountdown()
          const interval = setInterval(updateCountdown, 1000)
          // Store interval to clear on unmount
          setTimeout(() => clearInterval(interval), lockoutUntil - Date.now() + 1000)
        }
      } else if (data.error === 'INVALID_PASSWORD') {
        // Invalid password but not locked out yet
        setError('Falsches Passwort. Bitte versuchen Sie es erneut.')
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.')
      }
    } catch (err) {
      console.error('Login error:', err)
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.container}>
      <Paper className={styles.paper} p="xl" shadow="md">
        <Stack gap="md">
          <ResponsiveTitle order={2} ta="center" className={styles.title}>
            Admin-Anmeldung
          </ResponsiveTitle>
          
          <Text size="sm" c="dimmed" ta="center">
            Bitte geben Sie das Admin-Passwort ein
          </Text>

          <form onSubmit={handleLogin}>
            <Stack gap="md">
              <PasswordInput
                label="Passwort"
                placeholder="Admin-Passwort eingeben"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                error={error}
                size="md"
                disabled={isLockedOut}
              />

              {isLockedOut ? (
                <Alert 
                  icon={<IconLock size={16} />} 
                  color="red" 
                  title="Zu viele fehlgeschlagene Versuche"
                >
                  {remainingSeconds > 0
                    ? `Sie wurden für ${remainingSeconds} Sekunden gesperrt. Bitte versuchen Sie es später erneut.`
                    : 'Sie wurden für 1 Minute gesperrt. Bitte versuchen Sie es später erneut.'}
                </Alert>
              ) : (
                <Button 
                  type="submit" 
                  fullWidth 
                  size="md"
                  loading={loading}
                  className={styles.loginButton}
                >
                  Anmelden
                </Button>
              )}
            </Stack>
          </form>
        </Stack>
      </Paper>
    </div>
  )
}
